import type { ALL_LOCATIONS_FOR_SEARCH_QUERY_RESULT } from '@/sanity/types'
import type { Geolocation } from '@/lib/server/geolocation/types'

// Remove common zero-width / BOM characters that can sneak into copy-pasted text.
const ZERO_WIDTH_CHARS_REGEX = /[\u200B-\u200D\uFEFF]/g

export function normalizeRegionName(value: string): string {
  return value.replace(ZERO_WIDTH_CHARS_REGEX, '').trim()
}

/**
 * Format location name by removing "N " prefix
 * Example: "N Angel" -> "Angel"
 */
export function formatLocationName(name: string | null | undefined): string {
  if (!name) return ''
  return name.replace(/^N\s+/, '').trim()
}

/**
 * Calculate distance between two coordinates using Haversine formula
 * Returns distance in miles
 */
export function calculateDistance(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const R = 3959 // Earth's radius in miles
  const dLat = ((lat2 - lat1) * Math.PI) / 180
  const dLon = ((lon2 - lon1) * Math.PI) / 180
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2)
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  return R * c
}

/**
 * Filter locations within a radius of a search location
 */
export function filterLocationsByRadius(
  locations: ALL_LOCATIONS_FOR_SEARCH_QUERY_RESULT,
  searchLocation: Geolocation,
  radiusMiles: number
): Array<ALL_LOCATIONS_FOR_SEARCH_QUERY_RESULT[number] & { distance: number }> {
  if (!searchLocation.latitude || !searchLocation.longitude) {
    return []
  }

  const results: Array<
    ALL_LOCATIONS_FOR_SEARCH_QUERY_RESULT[number] & { distance: number }
  > = []

  for (const location of locations) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const coords = (location as any).coordinates as
      | { lat?: number; lng?: number }
      | undefined
    if (!coords?.lat || !coords?.lng) {
      continue
    }

    const distance = calculateDistance(
      searchLocation.latitude,
      searchLocation.longitude,
      coords.lat,
      coords.lng
    )

    if (distance <= radiusMiles) {
      results.push({ ...location, distance })
    }
  }

  // Sort by distance
  return results.sort((a, b) => a.distance - b.distance)
}

/**
 * Filter locations by region names
 */
export function filterLocationsByRegion(
  locations: ALL_LOCATIONS_FOR_SEARCH_QUERY_RESULT,
  regionNames: string[]
): ALL_LOCATIONS_FOR_SEARCH_QUERY_RESULT {
  if (regionNames.length === 0) {
    return locations
  }

  const normalizedRegionNames = regionNames
    .map((name) => normalizeRegionName(name).toLowerCase())
    .filter((name) => name.length > 0)

  return locations.filter((location) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const regions = (location as any).region as
      | Array<{ name?: string | null }>
      | null
      | undefined
    if (!regions || regions.length === 0) {
      return false
    }

    return regions.some((region) =>
      normalizedRegionNames.includes(
        region.name ? normalizeRegionName(region.name).toLowerCase() : ''
      )
    )
  })
}

/**
 * Filter locations by amenity names (IDs)
 */
export function filterLocationsByAmenity(
  locations: ALL_LOCATIONS_FOR_SEARCH_QUERY_RESULT,
  amenityIds: string[]
): ALL_LOCATIONS_FOR_SEARCH_QUERY_RESULT {
  if (amenityIds.length === 0) {
    return locations
  }

  return locations.filter((location) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const amenities = (location as any).amenities as
      | Array<{ _id?: string; name?: string | null }>
      | null
      | undefined
    if (!amenities || amenities.length === 0) {
      return false
    }

    return amenities.some((amenity) =>
      amenityIds.some((id) => amenity._id === id)
    )
  })
}

/**
 * Filter locations by access features
 * Note: This assumes access features will be added to the location schema
 */
export function filterLocationsByAccess(
  locations: ALL_LOCATIONS_FOR_SEARCH_QUERY_RESULT,
  accessFeatures: {
    allDayParking?: boolean
    bikeLockup?: boolean
    dropOffParking?: boolean
    stepFreeAccess?: boolean
  }
): ALL_LOCATIONS_FOR_SEARCH_QUERY_RESULT {
  // For now, return all locations as access features aren't in schema yet
  // This function is a placeholder for future implementation
  return locations
}
